import BugReportIcon from '@mui/icons-material/BugReport'
import EmailIcon from '@mui/icons-material/Email'
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Typography } from '@mui/material'
import React from 'react'
import { IoLogoWechat } from 'react-icons/io5'

import { isWeChatBrowser } from '../../utils/utility'

/**
 * Bug Report Dialog Component
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether to show the dialog
 * @param {function} props.onClose - Handler function to close dialog
 * @param {Object} props.user - Current user object
 * @param {string} props.version - Application version
 */
export default function BugReportDialog({ open, onClose, user, version }) {
  const isWechat = isWeChatBrowser()
  const currentUrl = window.location.href
  const userAgent = navigator.userAgent

  // Build email report function
  const handleEmailReport = () => {
    // Build email content with basic debug info
    const subject = 'Bug Report - Booking On Snow'
    const body = `
Please describe the issue:
[Enter issue description here]

Steps to reproduce:
[Enter steps here]

Screenshots:
It would be very helpful if you could attach screenshots showing the error or issue you encountered.

-------- System Info --------
Page URL: ${currentUrl}
Browser Info: ${userAgent}
User: ${user ? `${user.firstName} ${user.lastName} (${user.email})` : 'Not logged in'} 
App Version: ${version || 'Unknown'}

Additional Context:
Please add any other context about the problem here.
`

    // Use mailto to open user's default email client
    window.location.href = `mailto:support@bookingonsnow.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BugReportIcon color="primary" />
        <Typography variant="h6">Report Bug</Typography>
      </DialogTitle>

      <DialogContent>
        <DialogContentText paragraph>
          🙏 <strong>Thank you so much for taking the time to report a bug!</strong> 🐛 Your feedback is incredibly valuable and helps us improve the app for everyone. We truly
          appreciate your help and will work hard to resolve the issue as quickly as possible. ✨
        </DialogContentText>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          Report Methods:
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IoLogoWechat color="success" />
            <Typography>
              Add WeChat: <strong>duqcyxwd</strong> to contact developer directly
            </Typography>
          </Box>

          <Divider>OR</Divider>

          {isWechat && (
            <Alert severity="info" sx={{ mb: 2 }}>
              检测到您正在使用微信浏览器。为了获得更好的体验，建议您在默认浏览器中打开本应用后再报告错误。
            </Alert>
          )}

          <Button variant="outlined" color="primary" startIcon={<EmailIcon />} onClick={handleEmailReport}>
            Send Bug Report Email
          </Button>
        </Box>

        <Typography variant="subtitle1" gutterBottom>
          Debug Info:
        </Typography>

        <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, fontSize: '0.875rem' }}>
          <Typography variant="body2" component="div">
            <strong>Current Page:</strong> {window.location.pathname}
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Browser:</strong> {navigator.userAgent.split(' ').slice(-2).join(' ')}
          </Typography>
          <Typography variant="body2" component="div">
            <strong>App Version:</strong> {version || 'Unknown'}
          </Typography>
          {user && (
            <Typography variant="body2" component="div">
              <strong>User ID:</strong> {user.id || 'Unknown'}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
